import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  Code2, Globe, ChevronDown, CheckCircle
} from "lucide-react";
import Navbar from "./Navbar";
import Footer from "./Footer";

const PageContainer = styled.div`
  padding-top: 120px;
  min-height: 100vh;
  background: var(--bg-dark);
  color: var(--text-main);
`;

const ContentSection = styled.section`
  padding: 80px 5%;
  max-width: 1200px;
  margin: 0 auto;
`;

const Title = styled(motion.h1)`
  font-size: clamp(2rem, 5vw, 3rem);
  margin-bottom: 40px;
  text-align: center;
  background: linear-gradient(to right, var(--text-main), var(--text-muted));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const ProjectGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 30px;
  margin-top: 40px;
`;

const ProjectCard = styled(motion.div)`
  border-radius: 24px;
  overflow: hidden;
  background: var(--bg-card);
  border: 1px solid var(--glass-border);
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const ProjectImage = styled.div`
  height: 250px;
  background-image: url(${props => props.img});
  background-size: cover;
  background-position: center;
  position: relative;
  
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(to top, rgba(0,0,0,0.8), transparent);
  }
`;

const ProjectInfo = styled.div`
  padding: 24px;
  background: var(--bg-card);
  flex: 1;
  h3 { margin-bottom: 8px; font-size: 1.25rem; }
  p { color: var(--text-muted); font-size: 0.9rem; line-height: 1.6; }
`;

const EcosystemSection = styled.section`
  padding: 100px 5%;
  max-width: 1400px;
  margin: 0 auto;
  position: relative;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 80px;
  
  .badge {
    display: inline-block;
    padding: 6px 16px;
    border-radius: 100px;
    background: rgba(99, 102, 241, 0.1);
    color: var(--primary);
    font-weight: 600;
    font-size: 0.85rem;
    text-transform: uppercase;
    letter-spacing: 2px;
    margin-bottom: 20px;
    border: 1px solid rgba(99, 102, 241, 0.2);
  }
`;

const BannerGrid = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 80px;
  width: 100%;
`;

const BannerCard = styled(motion.div)`
  border-radius: 40px;
  overflow: hidden;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.01) 100%);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid var(--glass-border);
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: ${props => props.reverse ? "row-reverse" : "row"};
  min-height: 550px;
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 24px -1px rgba(0, 0, 0, 0.2);

  &:hover {
    border-color: rgba(99, 102, 241, 0.4);
    box-shadow: 
      0 0 0 1px rgba(99, 102, 241, 0.2),
      0 20px 40px -20px rgba(0, 0, 0, 0.5),
      0 0 60px -10px rgba(99, 102, 241, 0.15);
    transform: translateY(-5px);
  }

  @media (max-width: 1024px) {
    flex-direction: column;
    min-height: auto;
  }
`;

const BannerImageContainer = styled.div`
  flex: 1.4;
  overflow: hidden;
  position: relative;
  min-height: 400px;
  display: flex;
  flex-direction: column;
`;

const BannerImage = styled.div`
  width: 100%;
  flex: 1;
  min-height: 400px;
  background-image: url(${props => props.img});
  background-size: cover;
  background-position: center;
  transition: transform 0.8s cubic-bezier(0.4, 0, 0.2, 1);
  
  ${BannerCard}:hover & {
    transform: scale(1.08);
  }
  
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: ${props => props.reverse
    ? "linear-gradient(to right, transparent 30%, var(--bg-dark) 100%)"
    : "linear-gradient(to left, transparent 30%, var(--bg-dark) 100%)"};
    opacity: 0.6;
    
    @media (max-width: 1024px) {
      background: linear-gradient(to top, var(--bg-dark) 0%, transparent 70%);
      opacity: 0.8;
    }
  }
`;

const BannerInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 60px;
  justify-content: center;
  
  h3 {
    font-size: 3rem;
    font-weight: 800;
    margin-bottom: 20px;
    letter-spacing: -1px;
    background: linear-gradient(to right, #fff, rgba(255,255,255,0.7));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
  
  p {
    font-size: 1.15rem;
    line-height: 1.8;
    color: var(--text-muted);
    margin-bottom: 40px;
    max-width: 500px;
  }
  
  .cta-group {
    display: flex;
    align-items: center;
    gap: 30px;
    margin-top: auto;
  }

  .cta-link {
    display: inline-flex;
    align-items: center;
    gap: 12px;
    color: var(--text-main);
    font-weight: 600;
    text-decoration: none;
    font-size: 1rem;
    transition: all 0.3s ease;
    cursor: pointer;
    
    span {
      display: inline-block;
      width: 40px;
      height: 1px;
      background: var(--primary);
      transition: width 0.3s ease;
    }
    
    &:hover {
      color: var(--primary);
      gap: 18px;
    }
  }

  .external-btn {
    padding: 10px 24px;
    border-radius: 100px;
    background: rgba(99,102,241,0.1);
    color: var(--primary);
    border: 1px solid rgba(99,102,241,0.2);
    font-weight: 600;
    font-size: 0.9rem;
    display: flex;
    align-items: center;
    gap: 8px;
    transition: all 0.3s ease;

    &:hover {
      background: var(--primary);
      color: white;
      transform: translateY(-2px);
    }
  }
`;

const ProjectBadge = styled.span`
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(8px);
  color: white;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  border: 1px solid rgba(255, 255, 255, 0.1);
  z-index: 2;
`;

const FilterContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-bottom: 60px;
  flex-wrap: wrap;
`;

const FilterButton = styled.button`
  background: ${props => props.active ? "var(--primary)" : "transparent"};
  color: ${props => props.active ? "white" : "var(--text-muted)"};
  border: 1px solid ${props => props.active ? "var(--primary)" : "var(--glass-border)"};
  padding: 10px 24px;
  border-radius: 30px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:hover {
    border-color: var(--primary);
    color: var(--text-main);
    transform: translateY(-2px);
  }
`;

const CTASection = styled(motion.div)`
  margin-top: 100px;
  padding: 80px 40px;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(168, 85, 247, 0.1) 100%);
  border: 1px solid var(--glass-border);
  border-radius: 32px;
  text-align: center;
  backdrop-filter: blur(10px);
`;

const Projects = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [filter, setFilter] = useState("All");
  const [projectsList, setProjectsList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetch((process.env.REACT_APP_API_URL || "") + "/api/projects")
      .then(res => res.json())
      .then(data => {
        const activeProjects = Array.isArray(data) ? data.filter(p => p.status !== "archived") : [];
        setProjectsList(activeProjects);
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setLoading(false);
      });
  }, []);

  const categories = ["All", ...new Set(projectsList.filter(p => !p.is_owned).map(p => p.category).filter(Boolean))];
  const filteredProjects = filter === "All"
    ? projectsList.filter(p => !p.is_owned)
    : projectsList.filter(p => !p.is_owned && p.category === filter);

  const ownProducts = projectsList.filter(p => !p.is_owned && p.is_owned); // This is a bit weird, usually is_owned means ownProducts

  // Wait, let's fix ownProducts logic to be sensible
  const ownProductsList = projectsList.filter(p => p.is_owned);

  return (
    <>
      <Navbar />
      <PageContainer>
        {ownProductsList.length > 0 && (
          <EcosystemSection>
            <SectionHeader>
              <motion.span className="badge">Our Ventures</motion.span>
              <Title as="h2">The Nexova Ecosystem</Title>
              <motion.p style={{ fontSize: "1.25rem", color: "var(--text-muted)", maxWidth: "700px", margin: "0 auto", lineHeight: "1.6" }}>
                We build, scale and invest in our own digital ecosystem of products that redefine modern web standards.
              </motion.p>
            </SectionHeader>
            <BannerGrid>
              <AnimatePresence mode="popLayout">
                {ownProductsList.map((project, i) => {
                  const isReverse = i % 2 !== 0;
                  return (
                    <BannerCard key={project.id || project.title} reverse={isReverse} onClick={() => navigate("/projects/" + project.slug)}>
                      <BannerInfo reverse={isReverse}>
                        <ProjectBadge>{project.category || "Product"}</ProjectBadge>
                        <h3>{project.title}</h3>
                        <p>{project.description && project.description.substring(0, 260)}...</p>
                        <div className="cta-group">
                          <Link to={"/projects/" + project.slug} className="cta-link" onClick={(e) => e.stopPropagation()}>
                             Learn more
                          </Link>
                          {project.external_link && (
                            <a href={project.external_link} target="_blank" rel="nofollow noopener noreferrer" className="external-btn" onClick={(e) => e.stopPropagation()}>
                              Visit Website <Globe size={16} />
                            </a>
                          )}
                        </div>
                      </BannerInfo>
                      <BannerImageContainer>
                        <BannerImage img={project.img || "/images/projects/nexfixer-preview.jpg"} reverse={isReverse} />
                      </BannerImageContainer>
                    </BannerCard>
                  );
                })}
              </AnimatePresence>
            </BannerGrid>
          </EcosystemSection>
        )}
        <ContentSection>
          <Title>{t("projects.title") || "Projects"}</Title>
          <FilterContainer>
            {categories.map(cat => (
              <FilterButton key={cat} active={filter === cat} onClick={() => setFilter(cat)}>
                {cat}
              </FilterButton>
            ))}
          </FilterContainer>
          <ProjectGrid>
            <AnimatePresence mode="popLayout">
              {filteredProjects.map((project) => (
                <ProjectCard key={project.title} onClick={() => navigate("/projects/" + project.slug)}>
                  <ProjectBadge>{project.category || "Project"}</ProjectBadge>
                  <ProjectImage img={project.img || "/images/projects/nexfixer-preview.jpg"} />
                  <ProjectInfo>
                    <h3>{project.title}</h3>
                    <p>{project.description && project.description.substring(0, 150)}...</p>
                  </ProjectInfo>
                </ProjectCard>
              ))}
              {loading && <div>Loading projects...</div>}
            </AnimatePresence>
          </ProjectGrid>
        </ContentSection>
      </PageContainer>
      <Footer />
    </>
  );
};

export default Projects;
